<template>
  <div class="bg-utel-v2-secondary p-5 mb-2 mt-3 pt-4 rounded" id="agregar_estudiante">
    <h4 class="title-secondary">Agregar colaborador</h4>
    <small>Completa el formulario para agregar un nuevo colaborador</small>
    
    <form @submit.prevent="handleSubmit" class="mt-4">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <!-- Company Selection -->
          <div v-if="empresas.length > 0" class="form-group mb-4">
            <label for="empresa" class="form-label">Seleccione una empresa</label>
            <select v-model="selectedUrl" class="form-control rounded-8 sin-borde py-2" required>
              <option value="">Seleccionar la empresa</option>
              <option v-for="client in empresas" :key="client.id" :value="client.non_massive_">
                {{ client.client }}
              </option>
            </select>
          </div>

          <!-- Form Fields -->
          <div class="form-group mb-4">
            <input 
              v-model="formData.firstname" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.firstname.error }"
              placeholder="Nombre del Usuario" 
              required
            />
            <div v-if="validation.firstname.error" class="invalid-feedback">
              {{ validation.firstname.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.lastname" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.lastname.error }"
              placeholder="Apellido del usuario" 
              required
            />
            <div v-if="validation.lastname.error" class="invalid-feedback">
              {{ validation.lastname.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.email" 
              type="email" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.email.error }"
              placeholder="Email de usuario" 
              @blur="validateEmail"
              required
            />
            <div v-if="validation.email.error" class="invalid-feedback">
              {{ validation.email.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.position" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              placeholder="Cargo" 
            />
          </div>

          <div class="form-group mb-4 position-relative">
            <input 
              v-model="formData.area" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              placeholder="Área" 
              @input="searchAreas"
              @blur="hideAreasList"
            />
            <ul v-if="showAreas && filteredAreas.length" class="autocomplete-list">
              <li v-for="area in filteredAreas" :key="area" @mousedown="selectArea(area)">
                {{ area }}
              </li>
            </ul>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.level" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              placeholder="Nivel" 
            />
          </div>

          <div class="form-group mb-4">
            <input 
              ref="phoneInput"
              v-model="formData.cellphone" 
              type="tel" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.cellphone.error }"
              placeholder="Celular"
              @input="validatePhone"
            />
            <div v-if="validation.cellphone.error" class="invalid-feedback">
              {{ validation.cellphone.message }}
            </div>
          </div>

          <div class="d-flex gap-3 justify-content-end">
            <button type="button" @click="resetForm" class="btn bg-utel-v2-secondary text-primary rounded-8">
              Cancelar
            </button>
            <button type="submit" :disabled="!isFormValid || isSubmitting" class="btn btn-primary rounded-8">
              {{ isSubmitting ? 'Procesando...' : 'Guardar colaborador' }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MIN_LENGTH = 9;

export default {
  name: 'CreateStudentForm',
  
  props: {
    empresas: { type: Array, default: () => [] },
    searcharea: { type: String, required: true },
    urlvalidateemail: { type: String, required: true },
    url: { type: String, required: true }
  },
  
  emits: ['student-created', 'form-reset'],
  
  data() {
    return {
      selectedUrl: this.url,
      formData: this.getEmptyUser(),
      filteredAreas: [],
      showAreas: false,
      phoneIti: null,
      isSubmitting: false,
      validation: {
        firstname: { error: false, message: '' },
        lastname: { error: false, message: '' },
        email: { error: false, message: '' },
        cellphone: { error: false, message: '' }
      }
    };
  },
  
  computed: {
    isFormValid() {
      return this.formData.firstname && 
             this.formData.lastname && 
             this.formData.email && 
             EMAIL_REGEX.test(this.formData.email) &&
             this.formData.cellphone &&
             this.formData.cellphone.length >= PHONE_MIN_LENGTH &&
             this.selectedUrl;
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.initPhone();
    });
  },
  
  methods: {
    getEmptyUser() {
      return {
        firstname: '',
        lastname: '',
        email: '',
        cellphone: '',
        level: '',
        position: '',
        area: '',
        role: 'Estudiante'
      };
    },
    
    async handleSubmit() {
      if (!this.isFormValid || this.isSubmitting) return;
      
      this.isSubmitting = true;
      
      try {
        const userData = { 
          ...this.formData,
          cellphone: this.phoneIti ? this.phoneIti.getNumber() : this.formData.cellphone
        };
        
        await axios.post(this.selectedUrl, userData);
        
        this.$emit('student-created', { ...this.formData });
        this.resetForm();
        
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        this.handleValidationErrors(errors);
      } finally {
        this.isSubmitting = false;
      }
    },
    
    handleValidationErrors(errors) {
      Object.keys(this.validation).forEach(field => {
        this.validation[field] = {
          error: !!errors[field],
          message: errors[field] ? errors[field][0] : ''
        };
      });
    },
    
    async searchAreas() {
      const query = this.formData.area;
      if (query.length >= 3) {
        try {
          const response = await axios.post(this.searcharea, { query });
          this.filteredAreas = response.data.map(item => item.area.trim());
          this.showAreas = true;
        } catch (error) {
          console.error('Error searching areas:', error);
          this.filteredAreas = [];
        }
      } else {
        this.filteredAreas = [];
        this.showAreas = false;
      }
    },
    
    selectArea(area) {
      this.formData.area = area;
      this.showAreas = false;
      this.filteredAreas = [];
    },
    
    hideAreasList() {
      setTimeout(() => {
        this.showAreas = false;
      }, 200);
    },
    
    async validateEmail() {
      const email = this.formData.email;
      
      if (!email) {
        this.validation.email = { error: true, message: 'El email es requerido' };
        return;
      }
      
      if (!EMAIL_REGEX.test(email)) {
        this.validation.email = { error: true, message: 'El formato del email no es válido' };
        return;
      }
      
      try {
        const response = await axios.post(this.urlvalidateemail, { email });
        if (!response.data) {
          this.validation.email = { error: true, message: 'El email ya está registrado' };
        } else {
          this.validation.email = { error: false, message: '' };
        }
      } catch (error) {
        console.error('Error validating email:', error);
      }
    },
    
    validatePhone() {
      const phone = this.formData.cellphone;
      if (phone.length < PHONE_MIN_LENGTH) {
        this.validation.cellphone = { 
          error: true, 
          message: `El número debe tener al menos ${PHONE_MIN_LENGTH} dígitos` 
        };
      } else {
        this.validation.cellphone = { error: false, message: '' };
      }
    },
    
    initPhone() {
      if (window.intlTelInput && this.$refs.phoneInput) {
        this.phoneIti = window.intlTelInput(this.$refs.phoneInput, {
          utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
          initialCountry: 'mx',
          separateDialCode: true,
          hiddenInput: 'full'
        });
      }
    },
    
    resetForm() {
      this.formData = this.getEmptyUser();
      Object.keys(this.validation).forEach(field => {
        this.validation[field] = { error: false, message: '' };
      });
      this.$emit('form-reset');
    }
  }
}
</script>

<style scoped>
.title-secondary {
  color: #494949;
  font-weight: bold;
}

.form-label {
  color: #1A0054;
  font-weight: 500;
}

.autocomplete-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  list-style: none;
  padding: 0;
  margin: 0;
}

.autocomplete-list li {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.autocomplete-list li:hover {
  background-color: #f5f5f5;
}

.autocomplete-list li:last-child {
  border-bottom: none;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
</style>